import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MessageCircle, X, Send } from "lucide-react";
import { useLang } from "../LanguageContext";
import { useSettings } from "../SettingsContext";
import { resolveSiteImage } from "../brand";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function makeSessionId() {
  return "qw-" + Math.random().toString(36).slice(2) + "-" + Date.now();
}

export default function ChatWidget() {
  const { t, lang } = useLang();
  const { settings } = useSettings();
  const logoUrl = resolveSiteImage(settings, "logo");
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(() => {
    const existing = localStorage.getItem("qw_chat_session");
    if (existing) return existing;
    const id = makeSessionId();
    localStorage.setItem("qw_chat_session", id);
    return id;
  });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  // welcome message refreshes when language changes
  useEffect(() => {
    setMessages([{ role: "assistant", text: t.chat.welcome }]);
  }, [lang, t.chat.welcome]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const send = async () => {
    const message = input.trim();
    if (!message || sending) return;
    setMessages((m) => [...m, { role: "user", text: message }]);
    setInput("");
    setSending(true);
    try {
      const res = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message,
        language: lang,
      });
      setMessages((m) => [...m, { role: "assistant", text: res.data.reply }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          text:
            lang === "fr"
              ? "Désolée, une erreur est survenue. Merci de réessayer."
              : "Sorry, something went wrong. Please try again.",
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Floating action button */}
      <button
        data-testid="chat-toggle-btn"
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? t.chat.close : t.chat.open}
        className="fixed bottom-6 right-6 z-50 h-16 w-16 flex items-center justify-center bg-primary text-primary-foreground shadow-2xl border border-primary hover:bg-background hover:text-primary transition-all duration-500"
        style={{ boxShadow: "0 12px 40px -8px rgba(214,163,144,0.45)" }}
      >
        {open ? <X size={22} strokeWidth={1.2} /> : <MessageCircle size={22} strokeWidth={1.2} />}
      </button>

      {/* Panel */}
      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-28 right-6 z-50 w-[92vw] sm:w-[400px] h-[560px] max-h-[80vh] flex flex-col bg-background border border-border shadow-2xl overflow-hidden"
        >
          <div className="px-6 py-5 border-b border-border bg-muted/40 flex items-center gap-3">
            <img src={logoUrl} alt="" className="h-10 w-10 object-contain rounded-full bg-foreground/95" />
            <div className="flex-1 leading-tight">
              <p className="font-serif text-base text-foreground">{t.chat.title}</p>
              <p className="text-[0.7rem] text-muted-foreground tracking-wider">{t.chat.subtitle}</p>
            </div>
          </div>

          <div ref={scrollRef} data-testid="chat-messages" className="flex-1 overflow-y-auto qw-scroll px-5 py-5 space-y-4">
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  data-testid={`chat-msg-${m.role}`}
                  className={`max-w-[82%] px-4 py-3 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-foreground border border-border"
                  }`}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="px-4 py-3 text-sm text-muted-foreground italic bg-muted border border-border">
                  {t.chat.thinking}
                </div>
              </div>
            )}
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="px-4 py-3 border-t border-border bg-muted/30 flex items-center gap-3"
          >
            <input
              data-testid="chat-input"
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t.chat.placeholder}
              className="qw-input flex-1 !py-2 !text-sm"
            />
            <button
              type="submit"
              data-testid="chat-send-btn"
              disabled={!input.trim() || sending}
              className="h-10 w-10 flex items-center justify-center bg-primary text-primary-foreground disabled:opacity-40 hover:bg-accent transition-all"
              aria-label={t.chat.send}
            >
              <Send size={16} strokeWidth={1.2} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
